import React from 'react'

export type IconName =
  | 'home'
  | 'questions'
  | 'review'
  | 'topics'
  | 'subtopics'
  | 'packs'
  | 'users'
  | 'referral'
  | 'articles'
  | 'transfer'
  | 'menu'
  | 'close'
  | 'edit'
  | 'delete'
  | 'sync'
  | 'approve'
  | 'assign'
  | 'detail'
  | 'access'
  | 'back'
  | 'forward'
  | 'search'
  | 'logout'
  | 'key'
  | 'save'
  | 'chevron-down'

interface Props extends React.SVGProps<SVGSVGElement> {
  name: IconName
  size?: number
}

const Icon = ({ name, size = 20, ...props }: Props) => {
  const common = {
    fill: 'none',
    stroke: 'currentColor',
    strokeLinecap: 'round' as 'round',
    strokeLinejoin: 'round' as 'round',
    strokeWidth: 1.9,
  }

  const paths: Record<IconName, React.ReactNode> = {
    home: <><path d="m3 11 9-8 9 8" /><path d="M5 10v10h14V10" /><path d="M9 20v-6h6v6" /></>,
    questions: <><circle cx="12" cy="12" r="9" /><path d="M9.8 9a2.4 2.4 0 0 1 4.6 1c0 1.8-2.4 2-2.4 3.7" /><path d="M12 17h.01" /></>,
    review: <><path d="M9 11 11 13 15 9" /><path d="M12 3a9 9 0 1 0 9 9" /><path d="M15 3h6v6" /><path d="m21 3-5 5" /></>,
    topics: <><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2Z" /></>,
    subtopics: <><path d="M4 6h16" /><path d="M4 12h10" /><path d="M4 18h7" /><circle cx="18" cy="12" r="2" /><circle cx="15" cy="18" r="2" /></>,
    packs: <><path d="m12 3 8 4-8 4-8-4 8-4Z" /><path d="m4 12 8 4 8-4" /><path d="m4 17 8 4 8-4" /></>,
    users: <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>,
    referral: <><circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><path d="m8.6 10.5 6.8-4" /><path d="m8.6 13.5 6.8 4" /></>,
    articles: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" /><path d="M14 2v6h6" /><path d="M8 13h8" /><path d="M8 17h6" /></>,
    transfer: <><path d="M7 7h13" /><path d="m16 3 4 4-4 4" /><path d="M17 17H4" /><path d="m8 21-4-4 4-4" /></>,
    menu: <><path d="M4 7h16" /><path d="M4 12h16" /><path d="M4 17h16" /></>,
    close: <><path d="m6 6 12 12" /><path d="M18 6 6 18" /></>,
    edit: <><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L8 18l-4 1 1-4Z" /></>,
    delete: <><path d="M3 6h18" /><path d="M8 6V4h8v2" /><path d="m19 6-1 15H6L5 6" /><path d="M10 11v5" /><path d="M14 11v5" /></>,
    sync: <><path d="M20 7h-5V2" /><path d="M20 7a8 8 0 0 0-13.7-2.8L4 6.5" /><path d="M4 17h5v5" /><path d="M4 17a8 8 0 0 0 13.7 2.8l2.3-2.3" /></>,
    approve: <><path d="m5 12 4 4L19 6" /><circle cx="12" cy="12" r="9" /></>,
    assign: <><circle cx="9" cy="7" r="4" /><path d="M2 21v-2a4 4 0 0 1 4-4h6" /><path d="M16 19h6" /><path d="M19 16v6" /></>,
    detail: <><path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4Z" /><path d="M8 9h8" /><path d="M8 13h5" /></>,
    access: <><circle cx="8" cy="15" r="4" /><path d="m11 12 9-9" /><path d="m15 3 3 3" /><path d="m17 5 2-2" /></>,
    back: <><path d="m15 18-6-6 6-6" /></>,
    forward: <><path d="m9 18 6-6-6-6" /></>,
    search: <><circle cx="11" cy="11" r="7" /><path d="m20 20-4-4" /></>,
    logout: <><path d="M10 17l5-5-5-5" /><path d="M15 12H3" /><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" /></>,
    key: <><circle cx="8" cy="15" r="4" /><path d="m11 12 9-9" /><path d="m16 4 4 4" /></>,
    save: <><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2Z" /><path d="M17 21v-8H7v8" /><path d="M7 3v5h8" /></>,
    'chevron-down': <path d="m6 9 6 6 6-6" />,
  }

  return (
    <svg aria-hidden="true" height={size} viewBox="0 0 24 24" width={size} {...common} {...props}>
      {paths[name]}
    </svg>
  )
}

export default Icon
